import { Loader2, CheckCircle2, ShieldCheck, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { Reveal } from "@/components/Section";
import { useReferralForm } from "@/hooks/useReferralForm";
import { ParticipantSection, NdisSection, DisabilitySection, CarerSection, ReferrerSection } from "@/components/referral/Sections";

const SECTIONS = ["Person requiring support", "NDIS information", "Disability & reason for referral", "Carer / guardian details", "Referrer details"];

const Success = ({ done, onReset }) => {
  const t = useT();
  return (
    <Reveal className="mt-12 card p-10 text-center max-w-2xl">
      <div data-testid="referral-success">
        <CheckCircle2 size={56} className="mx-auto text-[var(--coral)]" />
        <h2 className="mt-5 text-3xl font-bold">{t("Thank you for your referral")}</h2>
        <p className="mt-3 text-[var(--muted)]">{t("We've received the referral for")} <strong className="text-[var(--ink)]">{done.participant_name}</strong>. {t("Our intake team will be in touch soon.")}</p>
        <p className="mt-4 text-xs text-slate-400">{t("Reference:")} <span data-testid="referral-reference">{done.id}</span></p>
        <button onClick={onReset} className="btn btn-blue mt-8" data-testid="referral-new">{t("Submit another referral")}</button>
      </div>
    </Reveal>
  );
};

const SideNav = () => {
  const t = useT();
  return (
    <aside className="hidden lg:block lg:col-span-3">
      <div className="sticky top-32 space-y-6">
        <ol className="space-y-1">
          {SECTIONS.map((s, i) => (
            <li key={s}><a href={`#section-${i + 1}`} className="flex items-center gap-3 text-sm py-2 px-3 rounded-md text-[var(--muted)] hover:bg-white hover:text-[var(--blue)] transition-colors"><span className="w-6 h-6 rounded-full bg-white border border-slate-200 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>{t(s)}</a></li>
          ))}
        </ol>
        <div className="card p-5 flex gap-3 text-sm">
          <ShieldCheck className="text-[var(--blue)] shrink-0" size={20} />
          <p className="text-[var(--muted)]">{t("We always uphold your privacy and confidentiality. Information is used only to arrange your support.")}</p>
        </div>
      </div>
    </aside>
  );
};

const ReferralForm = ({ form, loading, onChange, onToggleFunding, onSubmit }) => {
  const t = useT();
  return (
    <form onSubmit={onSubmit} className="lg:col-span-9 space-y-6" data-testid="referral-form">
      <ParticipantSection f={form} onChange={onChange} />
      <NdisSection f={form} onChange={onChange} onToggleFunding={onToggleFunding} />
      <DisabilitySection f={form} onChange={onChange} />
      <CarerSection f={form} onChange={onChange} />
      <ReferrerSection f={form} onChange={onChange} />
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button type="submit" disabled={loading} className="btn btn-coral" data-testid="referral-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {t("Submit referral")}
        </button>
        <p className="text-xs text-[var(--muted)]">{t("By submitting you agree we may contact you about this referral.")}</p>
      </div>
    </form>
  );
};

export default function Referral() {
  const t = useT();
  const { form, loading, done, onChange, toggleFunding, reset, submit } = useReferralForm();

  return (
    <section className="relative bg-[var(--blue-tint)] py-16 lg:py-24 overflow-hidden" data-testid="referral-page">
      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="eyebrow">{t("Referral form")}</span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-[1.1]" data-testid="page-title">{t("Make a")} <span className="text-[var(--coral)]">{t("referral.")}</span></h1>
          <p className="mt-5 text-base md:text-lg text-[var(--muted)] leading-relaxed">{t("Please complete the form below for new referrals to Aid in Care.")} {t("Fields marked with * are required.")} {t("We'll be in touch within one business day.")}</p>
        </div>
        {done ? (
          <Success done={done} onReset={reset} />
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <SideNav />
            <ReferralForm form={form} loading={loading} onChange={onChange} onToggleFunding={toggleFunding} onSubmit={submit} />
          </div>
        )}
      </div>
    </section>
  );
}
